/**
 * Tipi del contratto REST verso l'API Core.
 *
 * Trascritti a mano da `docs/CONVENTIONS.md` (§3 entita', §5 endpoint e forme
 * JSON, §6 errori, §7 esiti di validazione). Non sono generati da OpenAPI di
 * proposito: il contratto e' il documento, non il runtime.
 *
 * Regola che governa tutti i tipi di questo file: **date, importi, percentuali e
 * pesi arrivano dall'API gia' formattati in italiano, come stringhe**
 * (CONVENTIONS §2.3). Nel back-office non si riformatta e non si ricalcola
 * nulla: le stringhe si mostrano, e i tipi qui sotto lo rendono esplicito con
 * gli alias `DataIt`, `DataOraIt`, `ImportoIt`, `PesoIt`.
 *
 * Il dominio resta in italiano (nodo, pubblicazione, versione, documento)
 * perche' e' vocabolario normativo e tradurlo introdurrebbe ambiguita' di
 * mappatura verso ANAC — CONVENTIONS §8.
 */

// --------------------------------------------------------------------------- //
// Alias di formato
// --------------------------------------------------------------------------- //

/** Data gia' formattata `gg/mm/yyyy`, es. `30/04/2026`. */
export type DataIt = string;

/** Data e ora gia' formattate `gg/mm/yyyy hh:mm`, es. `30/03/2026 18:35`. */
export type DataOraIt = string;

/** Importo gia' formattato `n.nnn,dd`, es. `1.456.456,58`. */
export type ImportoIt = string;

/** Percentuale gia' formattata `nn,dd` senza simbolo, es. `75,33`. */
export type PercentualeIt = string;

/** Peso file gia' formattato, es. `973,12 Kb`. */
export type PesoIt = string;

/** Identificativo stabile di nodo dell'alberatura, es. `atti_generali`. */
export type IdNodo = string;

// --------------------------------------------------------------------------- //
// Errori — CONVENTIONS §6
// --------------------------------------------------------------------------- //

/** Codici di errore stabili restituiti dall'API. Mai tradotti: sono per il client. */
export const CODICI_ERRORE = [
  "NON_TROVATO",
  "NON_AUTENTICATO",
  "NON_AUTORIZZATO",
  "CONFLITTO",
  "DATI_NON_VALIDI",
  "TROPPE_RICHIESTE",
  "ERRORE_INTERNO",
  // Codici applicativi della pipeline di pubblicazione.
  "PDF_DA_SCANSIONE",
  "PDF_UA_NON_CONFORME",
  "SCHEMA_ANAC_NON_VALIDO",
  "NODO_NON_APPLICABILE",
  "VERSIONE_IMMUTABILE",
  // Durata e archiviazione per documento, copie collegate (CONVENTIONS §9.17-§9.18).
  "DOCUMENTO_ARCHIVIATO",
  "DOCUMENTO_MAI_PUBBLICATO",
  "DOCUMENTO_NON_CORRENTE",
  "CORREZIONE_IN_BOZZA",
  "PUBBLICAZIONE_ARCHIVIATA",
  "COPIA_NON_SOSTITUIBILE",
] as const;

export type CodiceErrore = (typeof CODICI_ERRORE)[number];

/** Violazione puntuale su un campo, dentro `errore.dettagli`. */
export interface DettaglioErrore {
  campo: string;
  messaggio: string;
}

/** Corpo di ogni risposta di errore dell'API. */
export interface CorpoErrore {
  errore: {
    codice: CodiceErrore | string;
    messaggio: string;
    dettagli?: DettaglioErrore[];
  };
}

// --------------------------------------------------------------------------- //
// Autenticazione — CONVENTIONS §5
// --------------------------------------------------------------------------- //

export const RUOLI = ["super_admin", "redattore"] as const;
export type Ruolo = (typeof RUOLI)[number];

/** Profilo e perimetro dell'utente autenticato. */
export interface UtenteCorrente {
  id: number;
  /** Nome con cui l'utenza accede: e' cio' che la identifica a schermo. */
  nome_utente: string;
  /** Recapito facoltativo. */
  email: string | null;
  ruolo: Ruolo;
  ente_id: number | null;
  /** Nodi su cui il redattore puo' operare. Per il super admin puo' essere vuoto. */
  nodi_assegnati: IdNodo[];
  /**
   * Nome utente dell'amministratore che sta operando nei panni di questa
   * utenza, se la sessione e' un'impersonificazione.
   *
   * Serve a **dirlo a schermo**: chi si e' messo nei panni di un altro e lo
   * dimentica crede di agire come se', ed e' il modo piu' facile di fare danni
   * in buona fede. Va notato che le scritture fatte in impersonificazione non
   * portano alcuna traccia dell'amministratore: questo campo vive nella
   * sessione, non nelle righe scritte.
   */
  impersonato_da?: string | null;
  /** Scadenza dell'impersonificazione: un'ora dal rilascio. */
  impersonificazione_scade_il?: DataOraIt | null;
}

export interface RichiestaAccesso {
  nome_utente: string;
  password: string;
}

/** Risposta di `POST /api/v1/auth/login`. */
export interface RispostaAccesso {
  access_token: string;
  token_type: string;
  /** Scadenza del token, gia' formattata `gg/mm/yyyy hh:mm`. */
  scade_il: DataOraIt;
  utente: UtenteCorrente;
}

// --------------------------------------------------------------------------- //
// Enti e utenti — CONVENTIONS §3
// --------------------------------------------------------------------------- //

export interface Ente {
  id: number;
  denominazione: string;
  /** 11 cifre numeriche. */
  codice_fiscale?: string;
  /** `comune.[nome].[sigla].it`, senza `www` (C.SI.5.2). */
  dominio: string;
  ambito_anac?: string;
}

export interface Utente {
  id: number;
  ente_id: number | null;
  nome_utente: string;
  /** Recapito facoltativo: non serve per accedere. */
  email: string | null;
  nome_completo?: string | null;
  ruolo: Ruolo;
  attivo: boolean;
  nodi_assegnati?: IdNodo[];
}

/**
 * Corpo di `POST /api/v1/utenti`.
 *
 * `password` e' obbligatoria (minimo 10 caratteri): non esiste un flusso di
 * invito per posta elettronica lato API, l'utenza nasce gia' utilizzabile.
 * `ente_id` e' obbligatorio quando `ruolo` e' `redattore` — l'ente e' il primo
 * dei due livelli del suo perimetro — e deve invece essere nullo per il Super
 * Admin, che e' cross-ente.
 */
export interface RichiestaInvitoUtente {
  nome_utente: string;
  /** Facoltativa; se indicata dev'essere unica. */
  email?: string | null;
  password: string;
  nome_completo?: string | null;
  ruolo: Ruolo;
  ente_id: number | null;
  attivo?: boolean;
  nodi_assegnati: IdNodo[];
}

/**
 * Corpo di `PUT /api/v1/utenti/{id}`: solo i campi anagrafici.
 *
 * **Il perimetro di nodi non passa di qui**: l'endpoint rifiuta i campi non
 * previsti (`extra="forbid"`). Si usa `assegnaNodi` su
 * `PUT /api/v1/utenti/{id}/nodi`.
 */
export interface RichiestaAggiornamentoUtente {
  nome_utente?: string;
  /** `null` svuota il recapito, che e' facoltativo. */
  email?: string | null;
  password?: string;
  nome_completo?: string | null;
  ruolo?: Ruolo;
  ente_id?: number | null;
  attivo?: boolean;
}

/** Risposta di `PUT`/`GET /api/v1/utenti/{id}/nodi`: il perimetro risultante. */
export interface RispostaAssegnazioni {
  utente_id: number;
  nodi_assegnati: IdNodo[];
}

// --------------------------------------------------------------------------- //
// Alberatura
// --------------------------------------------------------------------------- //

/**
 * Nodo dell'alberatura AT.
 *
 * `figli` e' annidato fino al terzo livello: sezione → sottosezione → obbligo.
 * Sulle foglie l'API restituisce `figli: []`, non lo omette.
 */
export interface Nodo {
  id_nodo: IdNodo;
  livello: 1 | 2 | 3;
  id_padre: IdNodo | null;
  denominazione: string;
  riferimento_normativo: string;
  url: string;
  applicabile: boolean;
  data_ultimo_aggiornamento: DataOraIt | null;
  /**
   * Perche' l'obbligo non ha ancora un contenuto, detto al cittadino.
   *
   * Compare sul portale subito. La pubblicazione del primo contenuto la
   * cancella — se quel contenuto va poi in archivio non ritorna, va riscritta —,
   * e l'API la rifiuta su un obbligo che ha gia' contenuti pubblicati o che non
   * e' applicabile. Lo scrive chi opera sull'obbligo; l'API non dice chi.
   */
  nota_assenza?: string | null;
  /** Giorno dell'ultima modifica della nota, gia' in formato italiano. */
  nota_assenza_il?: DataIt | null;
  figli?: Nodo[];
}

/** Risposta di `GET /api/v1/nodi?ente=`. */
export interface RispostaAlberatura {
  ente: Ente;
  nodi: Nodo[];
}

/**
 * Corpo di `POST /api/v1/nodi` — solo Super Admin.
 *
 * `livello` **non** si invia: lo deriva l'API dal catalogo e da `id_padre`.
 * `denominazione` e' facoltativa e, per i nodi a catalogo, superflua: se omessa
 * viene presa dal catalogo ANAC, se presente deve coincidere carattere per
 * carattere con quella ufficiale. E' obbligatoria solo per i nodi liberi
 * ammessi sotto «Altri contenuti → Dati ulteriori».
 */
export interface RichiestaCreazioneNodo {
  ente_id: number;
  id_nodo: IdNodo;
  id_padre: IdNodo | null;
  denominazione?: string;
  riferimento_normativo?: string;
  applicabile?: boolean;
  ordine?: number;
}

export interface RichiestaAggiornamentoNodo {
  denominazione?: string;
  riferimento_normativo?: string;
  applicabile?: boolean;
  ordine?: number;
}

// --------------------------------------------------------------------------- //
// Pubblicazioni, versioni, documenti
// --------------------------------------------------------------------------- //

export const STATI_PUBBLICAZIONE = ["bozza", "pubblicato", "archiviato"] as const;
export type StatoPubblicazione = (typeof STATI_PUBBLICAZIONE)[number];

export const FORMATI_DOCUMENTO = ["json", "csv", "xml", "pdf", "xlsx"] as const;
export type FormatoDocumento = (typeof FORMATI_DOCUMENTO)[number];

export const ESITI_CONTROLLO = ["superato", "avviso", "fallito"] as const;
export type EsitoControllo = (typeof ESITI_CONTROLLO)[number];

/**
 * Stati complessivi della validazione di un documento.
 *
 * Trascritti dall'enumerazione che la pipeline scrive davvero
 * (`api/app/workers/pdf_check.py::StatoValidazione`), non dal riassunto in
 * prosa: l'elenco diceva «avvisi» dove l'API scrive `avviso`, e non prevedeva
 * `non_applicabile` — lo stato di un CSV, su cui i controlli PDF non hanno
 * nulla da dire. Un confronto con un valore che non arriva mai non fallisce:
 * tace, e un documento con avvisi risultava «validato».
 *
 * `in_corso` non significa «in coda»: significa **non conclusa**.
 */
export const STATI_VALIDAZIONE = [
  "in_corso",
  "superata",
  "avviso",
  "fallita",
  "non_applicabile",
] as const;
export type StatoValidazione = (typeof STATI_VALIDAZIONE)[number];

/** Singolo controllo della pipeline — CONVENTIONS §7. */
export interface ControlloValidazione {
  nome: string;
  esito: EsitoControllo;
  bloccante: boolean;
  messaggio: string;
  /**
   * Chi ha eseguito il controllo: `"veraPDF 1.26.2 · profilo PDF/UA-1"` oppure
   * `"euristica interna (veraPDF assente: …)"`.
   *
   * Facoltativo perche' gli esiti scritti prima che questo campo esistesse non
   * ce l'hanno, e un documento caricato mesi fa non deve rompere la pagina.
   * Assente significa **euristica**, mai validatore ufficiale: leggerlo al
   * contrario trasformerebbe vecchie presunzioni in certificazioni.
   */
  fonte?: string | null;
  /** Una riga per requisito mancante, gia' scritta come rimedio operativo. */
  dettagli?: string[];
  /**
   * Una voce breve per requisito mancante — «Lingua del documento non
   * dichiarata» — senza il rimedio. Facoltativa per la stessa ragione di
   * `fonte`: gli esiti scritti prima che esistesse non la portano.
   */
  mancanze?: string[];
}

export const STATI_ACCESSIBILITA = [
  "verificata",
  "presunta",
  "non_garantita",
  "non_verificata",
  "non_applicabile",
] as const;
export type StatoAccessibilita = (typeof STATI_ACCESSIBILITA)[number];

export const VALIDATORI_ACCESSIBILITA = ["verapdf", "euristica"] as const;
export type ValidatoreAccessibilita = (typeof VALIDATORI_ACCESSIBILITA)[number];

/** Contenuto di `Documento.esiti_validazione`. */
export interface EsitiValidazione {
  stato: StatoValidazione;
  eseguita_il?: DataOraIt;
  controlli?: ControlloValidazione[];
}

export interface Documento {
  id: number;
  titolo: string;
  formato: FormatoDocumento;
  peso_bytes: number;
  /** Peso gia' formattato dall'API: si mostra, non si ricalcola. */
  peso: PesoIt;
  data_pubblicazione?: DataIt;
  /** Generato dal sistema, mai scelto dall'utente. */
  nome_file?: string;
  url?: string;
  url_latest?: string;
  /**
   * Cartella che raggruppa il documento nella pagina pubblica; `null` per un
   * documento in radice dell'obbligo.
   *
   * Non compare in alcun indirizzo: `url` e `url_latest` restano gli stessi
   * prima e dopo uno spostamento. E' cio' che rende l'operazione innocua su un
   * documento gia' pubblicato.
   */
  cartella_id?: number | null;
  esiti_validazione?: EsitiValidazione;
  /**
   * Verdetto di accessibilita' del file, derivato dai controlli.
   *
   * **Derivato e mai digitato**: nessun endpoint di scrittura lo accetta, e
   * l'unico modo di cambiarlo e' sostituire il file o chiedere una riverifica.
   * `verificata` la produce solo veraPDF; cio' che supera la sola analisi
   * interna vale `presunta`, che non e' la stessa cosa.
   */
  accessibilita?: StatoAccessibilita;
  accessibilita_validatore?: ValidatoreAccessibilita | null;
  /** Istante dell'ultima verifica conclusa, gia' in formato italiano. */
  accessibilita_verificata_il?: string | null;
  /**
   * Decorrenza: prima pubblicazione del documento, o della sua famiglia se e'
   * una revisione. Nulla su un documento mai pubblicato.
   */
  pubblicato_dal?: DataIt | null;
  /** Durata scelta per il documento, in anni; nulla = quella dell'obbligo. */
  durata_anni?: number | null;
  /** Ultimo giorno di pubblicazione dichiarato, compreso, in alternativa alla durata. */
  fino_al?: DataIt | null;
  /**
   * Scadenza del file: entro questo giorno va caricata una versione
   * aggiornata.
   *
   * **Non e' la fine della pubblicazione**: passato il giorno il file resta
   * pubblicato e nasce l'allerta «da sostituire». La fine della pubblicazione
   * e' `fine_pubblicazione`, e porta in archivio.
   */
  valido_fino_al?: DataIt | null;
  /**
   * Su un sostituto in bozza (la correzione di «Sostituisci», senza indirizzo):
   * la scadenza del file **online** che prendera' il posto. Nullo altrove.
   *
   * Serve perche' nella revisione aperta il file online e' gia' rimpiazzato dalla
   * correzione: senza questo dato l'allerta «File da sostituire» spariva al
   * caricamento del sostituto, mentre online restava il file scaduto.
   */
  valido_fino_al_sostituito?: DataIt | null;
  /**
   * Ultimo giorno in pubblicazione corrente, compreso, **calcolato dall'API**
   * con la stessa formula del worker di archiviazione. Va preferito al calcolo
   * locale (`finePubblicazione` di `lib/scadenze.ts`), che ne e' il ripiego.
   */
  fine_pubblicazione?: DataIt | null;
  /**
   * Giorno in cui il documento e' uscito dalla pubblicazione corrente; nullo se
   * corrente. Resta accessibile ai suoi indirizzi, nell'archivio della pagina.
   */
  archiviato_il?: DataIt | null;
  /**
   * Lo stesso documento pubblicato in altri obblighi, caricato una volta sola.
   * Solo nella vista del redattore; vuoto per un documento caricato in un
   * obbligo solo, che e' il caso normale.
   */
  copie_collegate?: CopiaCollegata[];
}

/** Una copia di un documento, pubblicata in un altro obbligo. */
export interface CopiaCollegata {
  documento_id: number;
  id_nodo: IdNodo;
  denominazione: string;
  pubblicazione_id: number;
  stato: "in_bozza" | "pubblicato" | "archiviato";
  /** Vero se nella sua pubblicazione c'e' una versione di lavoro da pubblicare. */
  da_pubblicare: boolean;
}

/** Un obbligo in cui pubblicare la copia, nel campo `copie` del caricamento. */
export interface RichiestaCopia {
  id_nodo: IdNodo;
  durata_anni: number | null;
  fino_al: DataIt | null;
}

/** Un documento con il contesto che la ricerca gli restituisce. */
export interface DocumentoTrovato extends Documento {
  nodo?: { id_nodo: IdNodo; denominazione: string; url?: string | null } | null;
  pubblicazione?: {
    id: number;
    titolo: string;
    anno_riferimento: number;
    stato: StatoPubblicazione;
  } | null;
}

/** Esito di `GET /api/v1/documenti`. */
export interface ElencoDocumenti {
  documenti: DocumentoTrovato[];
  totale: number;
  /**
   * Quanti documenti per stato, sull'intero filtro e **non sulla pagina**.
   * Un conteggio ricavato dalla pagina direbbe «3 da correggere» a chi ne ha
   * dodici, cioe' mentirebbe esattamente a chi sta filtrando per correggerli.
   */
  conteggi: Record<string, number>;
}

export interface Versione {
  progressivo: number;
  data_prima_pubblicazione: DataIt;
  data_ultima_modifica: DataIt;
  url_versionato?: string;
}

export const ORIGINI_PUBBLICAZIONE = ["interna", "esterna"] as const;
export type OriginePubblicazione = (typeof ORIGINI_PUBBLICAZIONE)[number];

export interface Pubblicazione {
  id: number;
  titolo: string;
  nodo_id?: IdNodo;
  id_schema: string | null;
  anno_riferimento: number;
  stato: StatoPubblicazione;
  /**
   * Da dove arriva la bozza: compilata qui, oppure depositata dal sistema di
   * controllo e bonifica tramite il canale di ingresso.
   *
   * E' **descrittiva, non autorizzativa**: una proposta esterna non ha piu'
   * poteri di una bozza interna e non si pubblica da sola. Serve a chi rivede,
   * per sapere che cosa ha davanti.
   */
  origine?: OriginePubblicazione;
  /** Identificativo della proposta nel sistema mittente. */
  origine_riferimento?: string | null;
  riferimenti_normativi: string;
  obblighi: string;
  sezioni_collegate: IdNodo[];
  data_ultimo_aggiornamento: DataOraIt | null;
  versioni?: Versione[];
  /**
   * Primo giorno in cui la pubblicazione **intera** esce dalla navigazione
   * corrente, calcolato dall'API: il giorno dopo l'ultima fine fra i suoi
   * documenti, o — senza documenti — il 1º gennaio dopo la durata
   * dell'obbligo (cinque anni, art. 8 c. 3, se il Comune non ne ha dichiarata
   * un'altra). E' la formula che il worker di archiviazione applica.
   *
   * Facoltativa per non rompere il client contro un'API non aggiornata: in quel
   * caso si ricade su `dataArchiviazionePrevista` di `lib/scadenze.ts`.
   */
  data_archiviazione_prevista?: DataIt | null;
  /**
   * Vero se esiste una versione di lavoro non ancora pubblicata.
   *
   * **Non e' deducibile dal resto della risposta**, ed e' la ragione per cui
   * esiste: `stato` resta `pubblicato` per tutta la lavorazione di una
   * revisione — dal secondo caricamento sullo stesso obbligo l'API riapre la
   * pubblicazione esistente invece di affiancarne una seconda — e `versioni`
   * elenca le sole versioni pubblicate. Senza questo campo «pubblicato» e
   * «pubblicato con una revisione pronta» sono indistinguibili, e il comando di
   * pubblicazione non verrebbe offerto proprio nel caso piu' comune.
   *
   * Facoltativo per non rompere il client contro un'API non ancora aggiornata:
   * assente vale come `false`.
   */
  versione_di_lavoro_aperta?: boolean;
  documenti?: Documento[];
}

/**
 * Ripartizione facoltativa dei documenti dentro un obbligo.
 *
 * Non e' un quarto livello di alberatura: non ha un URL proprio, non compare
 * nella at_map e non entra nell'indirizzo dei documenti che raggruppa. Il
 * vincolo `livello IN (1,2,3)` sui nodi resta intatto, ed e' voluto — `id_nodo`
 * e' la chiave della at_map e ANAC ne pretende la stabilita'.
 *
 * `figlie` e' annidato liberamente: l'API impone un tetto di profondita', non
 * una struttura fissa.
 */
export interface Cartella {
  id: number;
  titolo: string;
  id_padre: number | null;
  ordine: number;
  /** Vero se il titolo e' un anno: la resa pubblica lo ordina per data. */
  e_annualita: boolean;
  /** Documenti direttamente dentro questa cartella. */
  documenti: number;
  /** Documenti inclusi quelli delle sottocartelle. */
  documenti_totali: number;
  figlie: Cartella[];
}

/** Corpo di `POST /api/v1/nodi/{id_nodo}/cartelle`. */
export interface RichiestaCreazioneCartella {
  titolo: string;
  id_padre?: number | null;
  ordine?: number;
}

/**
 * Corpo di `PUT /api/v1/cartelle/{id}`.
 *
 * `id_padre` si distingue **per presenza**, non per valore: omesso lascia la
 * cartella dov'e', inviato a `null` la riporta in radice dell'obbligo.
 */
export interface RichiestaAggiornamentoCartella {
  titolo?: string;
  id_padre?: number | null;
  ordine?: number;
}

/** Risposta di `GET /api/v1/nodi/{id_nodo}/pubblicazioni?anno=`. */
export interface RispostaPubblicazioniNodo {
  nodo: Pick<Nodo, "id_nodo" | "denominazione" | "nota_assenza" | "nota_assenza_il">;
  anni_disponibili: number[];
  anno_selezionato: number | null;
  pubblicazioni: Pubblicazione[];
  /**
   * Ripartizione dell'obbligo. Lista vuota quando il Comune non la usa, che e'
   * il caso piu' comune. Arriva insieme ai contenuti e non da una chiamata a
   * parte: la pagina si disegna con una richiesta sola, altrimenti una delle
   * due risposte puo' mancare e la pagina si compone a meta'.
   */
  cartelle?: Cartella[];
}

/** Risposta di `GET /api/v1/pubblicazioni`. */
export interface RispostaElencoPubblicazioni {
  pubblicazioni: Pubblicazione[];
  totale?: number;
}

/** Corpo di `POST /api/v1/pubblicazioni`. */
export interface RichiestaCreazionePubblicazione {
  nodo_id: IdNodo;
  anno_riferimento: number;
  titolo: string;
  id_schema: string | null;
  riferimenti_normativi: string;
  obblighi: string;
  sezioni_collegate: IdNodo[];
  /** Payload conforme allo schema ANAC applicabile, con i valori gia' in formato italiano. */
  dati_strutturati: Record<string, string> | null;
}

/** Risposta di `POST /api/v1/pubblicazioni/{id}/pubblica`. */
export interface RispostaPubblicazioneEseguita {
  pubblicazione: Pick<Pubblicazione, "id" | "stato">;
  versione: Versione;
  at_map_rigenerata: boolean;
}

// --------------------------------------------------------------------------- //
// Allerte sui contenuti scaduti: verdetti e natura degli obblighi
//
// Le allerte le calcola il back-office (`lib/allerte.ts`) e non l'API, perche'
// dipendono dalla periodicita' degli obblighi, che non e' un dato di questo
// sistema. Qui vivono i soli tipi di cio' che si **registra**: la decisione
// dell'operatore, che e' il dato mancante.
// --------------------------------------------------------------------------- //

export const TIPI_ALLERTA = [
  "annualita_senza_successore",
  "quinquennio",
  "aggiornamento_scaduto",
  "obbligo_scoperto",
  "documento_da_sostituire",
] as const;
export type TipoAllerta = (typeof TIPI_ALLERTA)[number];

export const ESITI_VALUTAZIONE = [
  "sostituto_non_dovuto",
  "sostituto_in_preparazione",
  // La riga che il giornale scrive da se' a ogni dichiarazione di regole
  // dell'obbligo. Non risponde a un'allerta e non si invia da qui.
  "regola_dichiarata",
] as const;
export type EsitoValutazione = (typeof ESITI_VALUTAZIONE)[number];

export const PORTATE_VALUTAZIONE = ["periodo", "obbligo"] as const;
export type PortataValutazione = (typeof PORTATE_VALUTAZIONE)[number];

export const NATURE_OBBLIGO = ["sostitutivo", "accumulativo", "non_ricorrente"] as const;
export type NaturaObbligo = (typeof NATURE_OBBLIGO)[number];

export const CADENZE_OBBLIGO = [
  "tempestivo",
  "trimestrale",
  "semestrale",
  "annuale",
] as const;
export type CadenzaObbligo = (typeof CADENZE_OBBLIGO)[number];

/** Una riga del giornale delle decisioni. Immutabile: si revoca, non si corregge. */
export interface Valutazione {
  id: number;
  id_nodo: IdNodo;
  tipo_allerta: TipoAllerta | null;
  /** Periodo a cui il verdetto si riferisce, nella forma prodotta da `lib/allerte.ts`. */
  chiave_periodo: string | null;
  pubblicazione_id: number | null;
  esito: EsitoValutazione;
  portata: PortataValutazione;
  /** Natura proposta dal redattore: la conferma resta al Super Admin. */
  natura_proposta: NaturaObbligo | null;
  motivo: string;
  valida_fino_al: DataIt | null;
  /** Istantanea delle evidenze al momento della decisione, mostrata verbatim. */
  evidenza?: Record<string, unknown> | null;
  /** Nome utente di chi ha deciso. */
  decisa_da: string;
  decisa_il: DataOraIt;
  revocata_il: DataOraIt | null;
  revocata_da: string | null;
  motivo_revoca: string | null;
  /** Calcolata dall'API: non revocata e non scaduta. */
  attiva: boolean;
}

/** Corpo di `POST /api/v1/valutazioni`. */
export interface RichiestaValutazione {
  id_nodo: IdNodo;
  tipo_allerta?: TipoAllerta | null;
  chiave_periodo?: string | null;
  pubblicazione_id?: number | null;
  esito: EsitoValutazione;
  portata: PortataValutazione;
  natura_proposta?: NaturaObbligo | null;
  motivo: string;
  valida_fino_al?: DataIt | null;
  evidenza?: Record<string, unknown> | null;
}

/** La natura dichiarata di un obbligo: il dato che il catalogo ANAC non da'. */
export interface RegolaObbligo {
  id_nodo: IdNodo;
  /** Nulla quando il Comune ha dichiarato solo la cadenza o la durata. */
  natura: NaturaObbligo | null;
  /** Nullo significa «si tiene l'ipotesi ereditata dalla sezione». */
  cadenza_dichiarata: CadenzaObbligo | null;
  /**
   * Durata di pubblicazione predefinita dei documenti dell'obbligo, in anni.
   * Nulla: i cinque anni dell'art. 8 c. 3. Facoltativa per un'API non
   * aggiornata.
   */
  durata_anni?: number | null;
  motivo: string;
  valutazione_id: number | null;
  dichiarata_da: string;
  dichiarata_il: DataOraIt;
  aggiornata_il: DataOraIt;
  /**
   * Solo nella risposta a una dichiarazione che cambia la durata: quanti
   * documenti la durata nuova ha portato oltre la fine, e che per questo sono
   * usciti subito dalla pagina (archiviati, non cancellati).
   */
  documenti_archiviati?: number | null;
  /** I documenti oltre la fine che non si sono potuti archiviare, con il perche'. */
  archiviazioni_non_riuscite?: string[];
  /**
   * Solo nella risposta a una dichiarazione: quanti documenti dell'obbligo
   * restano online oltre la fine — quelli non archiviabili, o, a durata
   * invariata, quelli che il worker non ha ancora raggiunto.
   */
  documenti_oltre_la_fine?: number | null;
}

/** Corpo di `PUT /api/v1/nodi/{id_nodo}/regola` — solo Super Admin. */
export interface RichiestaRegola {
  /**
   * Per tutti e tre i campi: **omesso resta il valore vigente**, `null` lo
   * toglie. Chi dichiara la natura da un'allerta non manda la durata, e cosi'
   * non la cancella; chi non sceglie una cadenza non la manda.
   */
  natura?: NaturaObbligo | null;
  cadenza_dichiarata?: CadenzaObbligo | null;
  durata_anni?: number | null;
  motivo: string;
  valutazione_id?: number | null;
}

/** Risposta di `GET /api/v1/valutazioni`. */
export interface RispostaValutazioni {
  valutazioni: Valutazione[];
  totale?: number;
}

// --------------------------------------------------------------------------- //
// Schemi ANAC
// --------------------------------------------------------------------------- //

export interface SchemaAnac {
  id_schema: string;
  denominazione: string;
  riferimento_normativo: string;
  delibera: string;
  /** Data di decorrenza gia' formattata, es. `22/01/2026`. */
  decorrenza: DataIt;
  attivo: boolean;
  /** Descrittori dei campi, se l'API li espone. */
  campi?: CampoSchema[];
}

/** Tipo di campo elementare ANAC (tabella «Formato dei campi elementari»). */
export const TIPI_CAMPO = [
  "testo",
  "testo_lungo",
  "anno",
  "trimestre",
  "semestre",
  "data",
  "ora",
  "data_ora",
  "importo",
  "percentuale",
  "email",
  "url",
  "codice_fiscale_ente",
  "opzioni",
] as const;

export type TipoCampo = (typeof TIPI_CAMPO)[number];

/** Descrittore di un campo di schema ANAC, per generare il form di compilazione. */
export interface CampoSchema {
  nome: string;
  etichetta: string;
  tipo: TipoCampo;
  obbligatorio: boolean;
  aiuto?: string;
  opzioni?: readonly string[];
}

// --------------------------------------------------------------------------- //
// at_map — CONVENTIONS §4
// --------------------------------------------------------------------------- //

/** Contenuto di `GET /at_map.json`: id_nodo → URL pubblico. */
export type AtMap = Record<IdNodo, string>;

// --------------------------------------------------------------------------- //
// Assistente alla trasparenza
// --------------------------------------------------------------------------- //

/** Risposta di `GET /api/v1/assistente/stato`. */
export interface StatoAssistente {
  abilitato: boolean;
}

/** Risposta di `GET /api/v1/assistente/nodi/{id_nodo}/apertura`: dalla scheda, senza modello. */
export interface AperturaAssistente {
  id_nodo: IdNodo;
  titolo: string;
  riferimento_normativo: string;
  aggiornamento: string;
  durata: string;
  saluto: string;
  suggerimenti: string[];
}

/** Un turno della conversazione come lo riceve l'API. */
export interface MessaggioAssistente {
  ruolo: "utente" | "assistente";
  contenuto: string;
}

/** Corpo di `POST /api/v1/assistente/chat`. */
export interface RichiestaChatAssistente {
  id_nodo: IdNodo | null;
  ente?: number | null;
  messaggi: MessaggioAssistente[];
}
